import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Plus, Minus, Trash2, ShoppingBag } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { SHOW_PRICES } from '../config';
import PreorderCountdown from './PreorderCountdown';

// The basket, without leaving the menu. Adding a meal used to mean either
// staying put with no confirmation of what was in the basket, or navigating to
// the checkout and losing your place — neither of which is how a shop behaves.
export default function CartDrawer() {
  const { cart, getDish, tf, t, updateQty, removeFromCart, cartTotal, cartCount, cartOpen, setCartOpen } = useApp();
  const navigate = useNavigate();

  useEffect(() => {
    if (!cartOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setCartOpen(false); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [cartOpen, setCartOpen]);

  if (!cartOpen) return null;

  const goToCheckout = () => { setCartOpen(false); navigate('/commande'); };

  return (
    <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={() => setCartOpen(false)} />
      <aside className="absolute right-0 top-0 h-full w-full sm:w-[400px] bg-[#faf9f5] shadow-2xl flex flex-col">
        <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-neutral-200 bg-white">
          <h2 className="font-display text-xl text-[#2f3826]">{t('cart_title')}</h2>
          <div className="flex items-center gap-2">
            <PreorderCountdown compact />
            <button onClick={() => setCartOpen(false)} aria-label={t('close')} className="p-1.5 text-neutral-500 hover:text-[#4b5e34]">
              <X size={20} />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {cartCount === 0 ? (
            <div className="text-center py-12">
              <ShoppingBag size={28} className="mx-auto text-neutral-300 mb-3" />
              <p className="text-neutral-500 text-sm">{t('cart_empty')}</p>
              <p className="text-neutral-400 text-xs mt-1">{t('basket_empty_hint')}</p>
            </div>
          ) : cart.map((item) => {
            const d = getDish(item.id);
            if (!d) return null;
            return (
              <div key={item.id} className="flex gap-3 bg-white rounded-xl border border-neutral-200 p-3">
                <img src={d.image} alt={tf(d.name)} className="w-16 h-16 rounded-lg object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-[#2f3826] leading-snug line-clamp-2">{tf(d.name)}</h3>
                  {SHOW_PRICES && <p className="text-xs text-neutral-500 mt-0.5">CHF {d.price?.toFixed(2)}</p>}
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex items-center border border-neutral-300 rounded-lg">
                      <button onClick={() => updateQty(item.id, item.qty - 1)} aria-label="-" className="p-1 hover:bg-neutral-100"><Minus size={13} /></button>
                      <span className="w-7 text-center text-sm font-semibold">{item.qty}</span>
                      <button onClick={() => updateQty(item.id, item.qty + 1)} aria-label="+" className="p-1 hover:bg-neutral-100"><Plus size={13} /></button>
                    </div>
                    <button onClick={() => removeFromCart(item.id)} className="text-neutral-400 hover:text-red-500" aria-label={t('remove')}>
                      <Trash2 size={14} />
                    </button>
                    {SHOW_PRICES && (
                      <span className="ml-auto text-sm font-semibold text-[#2f3826]">CHF {(d.price * item.qty).toFixed(2)}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {cartCount > 0 && (
          <footer className="border-t border-neutral-200 bg-white px-5 py-4 space-y-3">
            {SHOW_PRICES && (
              <div className="flex items-center justify-between font-semibold">
                <span className="text-[#2f3826]">{t('total')}</span>
                <span className="font-display text-xl text-[#4b5e34]">CHF {cartTotal.toFixed(2)}</span>
              </div>
            )}
            <button onClick={goToCheckout} data-testid="drawer-checkout"
                    className="w-full bg-[#4b5e34] hover:bg-[#3f5230] text-white py-3 rounded-lg font-medium transition-colors">
              {t('finish_order')}
            </button>
            <button onClick={() => setCartOpen(false)} className="w-full text-sm text-[#4b5e34] hover:underline">
              + {t('keep_browsing')}
            </button>
          </footer>
        )}
      </aside>
    </div>
  );
}
